package br.com.vvrefeicoes.portal.feature.juridico

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.domain.model.Company
import br.com.vvrefeicoes.portal.domain.model.JuridicoHistoricoSyncStatus
import br.com.vvrefeicoes.portal.domain.model.JuridicoProcesso
import br.com.vvrefeicoes.portal.domain.repository.JuridicoCreditoRepository
import br.com.vvrefeicoes.portal.domain.repository.JuridicoProcessoRepository
import br.com.vvrefeicoes.portal.domain.repository.OrgChartRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

class JuridicoProcessosController(
    private val repository: JuridicoProcessoRepository,
    private val creditoRepository: JuridicoCreditoRepository,
    private val orgChartRepository: OrgChartRepository,
    private val scope: CoroutineScope,
) {
    var processos by mutableStateOf<List<JuridicoProcesso>>(emptyList())
        private set
    var allFilteredProcessos by mutableStateOf<List<JuridicoProcesso>>(emptyList())
        private set
    var loading by mutableStateOf(false)
        private set
    var syncingHistorical by mutableStateOf(false)
        private set
    var historicalSyncStatus by mutableStateOf(JuridicoHistoricoSyncStatus())
        private set
    var errorMessage by mutableStateOf<String?>(null)
        private set
    var credits by mutableStateOf<Double?>(null)
        private set
    var page by mutableStateOf(0)
        private set
    var totalPages by mutableStateOf(1)
        private set
    var numeroFiltro by mutableStateOf("")
    var tribunalFiltro by mutableStateOf("")
    var statusFiltro by mutableStateOf("")
    var dataMinimaFiltro by mutableStateOf("")
    var dataMaximaFiltro by mutableStateOf("")
    var availableUnits by mutableStateOf<List<Company>>(emptyList())
        private set
    var statusOptions by mutableStateOf<List<String>>(emptyList())
        private set
    var tribunalOptions by mutableStateOf<List<String>>(emptyList())
        private set
    var processoParaUnidades by mutableStateOf<JuridicoProcesso?>(null)
        private set
    var savingUnits by mutableStateOf(false)
        private set

    fun init() = load()

    fun applyFilters() {
        page = 0
        load()
    }

    fun goToPage(value: Int) {
        page = value.coerceIn(0, totalPages - 1)
        load()
    }

    fun load() {
        if (loading) return
        loading = true
        scope.launch {
            try {
                historicalSyncStatus = runCatching { repository.historicalSyncStatus() }.getOrDefault(historicalSyncStatus)
                availableUnits =
                    runCatching {
                        orgChartRepository
                            .loadInitial()
                            .currentVersion.data.companies
                            .filter { !it.inactive && it.organogramaUnidadeId.isNotBlank() }
                    }.getOrDefault(availableUnits)
                // Credit availability must not prevent the process list from loading.
                credits = runCatching { creditoRepository.credits() }.getOrNull()
                val filters = listOf(numeroFiltro, tribunalFiltro, statusFiltro, dataMinimaFiltro.toApiDate(), dataMaximaFiltro.toApiDate())
                val result = repository.list(filters[0], filters[1], filters[2], filters[3], filters[4], page, 20)
                processos = result.content
                totalPages = result.totalPages.coerceAtLeast(1)
                allFilteredProcessos = runCatching { loadAllFilteredProcessos(filters) }.getOrDefault(result.content)
                if (statusOptions.isEmpty() && tribunalOptions.isEmpty()) {
                    runCatching { repository.filterOptions() }.getOrNull()?.let {
                        statusOptions = it.status
                        tribunalOptions = it.tribunais
                    }
                }
                errorMessage = null
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível carregar os processos."
            } finally {
                loading = false
            }
        }
    }

    fun openUnitLink(processo: JuridicoProcesso) {
        processoParaUnidades = processo
    }

    fun closeUnitLink() {
        if (!savingUnits) processoParaUnidades = null
    }

    fun saveUnitLink(unitIds: List<String>) {
        val processo = processoParaUnidades ?: return
        if (savingUnits) return
        savingUnits = true
        scope.launch {
            try {
                val updated = repository.updateUnits(processo.id, unitIds)
                processos = processos.map { if (it.id == updated.id) updated else it }
                allFilteredProcessos = allFilteredProcessos.map { if (it.id == updated.id) updated else it }
                processoParaUnidades = null
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível vincular as unidades."
            } finally {
                savingUnits = false
            }
        }
    }

    fun synchronizeHistorical() {
        if (syncingHistorical || loading) return
        syncingHistorical = true
        scope.launch {
            try {
                repository.synchronizeHistorical()
                historicalSyncStatus = repository.historicalSyncStatus()
                load()
            } catch (exception: Exception) {
                errorMessage = exception.message ?: "Não foi possível sincronizar o histórico."
            } finally {
                syncingHistorical = false
            }
        }
    }

    fun exportCsv() {
        if (!exportProcessosCsv(allFilteredProcessos)) errorMessage = "Não foi possível exportar os processos."
    }

    fun exportPdf() {
        if (!exportProcessosPdf(allFilteredProcessos)) errorMessage = "Não foi possível exportar os processos."
    }

    private suspend fun loadAllFilteredProcessos(filters: List<String>): List<JuridicoProcesso> {
        val firstPage = repository.list(filters[0], filters[1], filters[2], filters[3], filters[4], 0, 100)
        val remainingPages =
            (1 until firstPage.totalPages.coerceAtLeast(1)).map { pageIndex ->
                repository.list(filters[0], filters[1], filters[2], filters[3], filters[4], pageIndex, 100).content
            }
        return buildList {
            addAll(firstPage.content)
            remainingPages.forEach(::addAll)
        }
    }
}

@Composable
fun rememberJuridicoProcessosController(): JuridicoProcessosController {
    val scope = rememberCoroutineScope()
    val repository = LocalAppGraph.current.juridicoProcessoRepository
    val creditoRepository = LocalAppGraph.current.juridicoCreditoRepository
    val orgChartRepository = LocalAppGraph.current.orgChartRepository
    return remember { JuridicoProcessosController(repository, creditoRepository, orgChartRepository, scope) }
}
